import { HelpCircle } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * HelpTooltip - A reusable contextual help tooltip component
 * 
 * Usage:
 * <HelpTooltip content="This is the help text" />
 * <HelpTooltip content="Help text" side="right" />
 */
const HelpTooltip = ({ 
  content, 
  side = "top", 
  className = "",
  iconSize = "h-4 w-4"
}) => {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button 
            type="button"
            className={`inline-flex items-center justify-center text-slate-400 hover:text-blue-600 transition-colors focus:outline-none ${className}`}
            onClick={(e) => e.preventDefault()}
          >
            <HelpCircle className={iconSize} />
          </button>
        </TooltipTrigger>
        <TooltipContent 
          side={side} 
          className="max-w-xs bg-slate-900 text-white text-sm p-3 rounded-lg shadow-lg"
        >
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Pre-defined help content for common configurator fields
 */
export const HELP_CONTENT = {
  // General
  projectName: "Enter a name to identify this quote/project. This will appear on saved quotes and exports.",
  quoteReference: "Optional reference number for your internal tracking (e.g., PO number, project code).",
  priceModifier: "Apply a percentage discount to the total quote. Distributors can offer project-specific pricing.",
  
  // Speedlanes
  laneCount: "The number of pedestrian lanes needed. Each lane requires a master unit, with slave units for additional lanes.",
  glassHeight: "Standard (1000mm) for normal security. Mid Height (1300mm) or Full Height (1800mm) for enhanced security to prevent climbing.",
  biometrics: "Choose verification method: Face recognition (contactless), Fingerprint (traditional), Card (RFID), or QR (mobile/visitor).",
  
  // Atlas Access Control
  credentialType: "RFID: Proximity cards. Biometric: Fingerprint readers. Wiegand: Third-party reader integration.",
  readInOut: "Read In Only: Reader on entry side, free exit. Read In/Out: Readers on both sides for full tracking.",
  doorCount: "Total number of doors to control. System calculates optimal controller configuration automatically.",
  
  // Bio CVSecurity
  adminLicenses: "Number of admin users who can manage the system. Each admin needs a license.",
  modules: "Select which Bio CVSecurity modules you need. Access Control is the core module.",
  bluetoothLicense: "Annual: Reassignable subscription. Permanent: One-time purchase tied to specific device.",
  
  // Parking Barriers
  barrierType: "Standard: Basic barrier. LED Boom: Illuminated boom arm for better visibility.",
  boomLength: "Select based on your lane width. Ensure boom clears the full lane when raised.",
  handed: "Determined by mounting position relative to traffic flow. Face the barrier from the approach direction.",
  lprCamera: "License Plate Recognition camera for automated vehicle identification.",
  
  // Full Height Turnstiles
  turnstileType: "Single: One passage, one direction. Double: One passage, bi-directional access.",
  armConfig: "3-arm (120°): Faster throughput. 4-arm (90°): Better anti-passback control.",
  finishType: "Powder Coated: Durable colored finish. Stainless Steel: Premium appearance, weather resistant.",
  
  // Saving & Exporting
  createQuote: "Save this configuration to your account. You can access it later from Saved Quotes.",
  downloadQuote: "Download an image of this quote for sharing via email or messaging.",
  exportCsv: "Export the Bill of Materials to CSV format for use in spreadsheets."
};

export default HelpTooltip;
